// Feature flags: switch whole game systems on or off. Defaults live here; `?flags=` on the URL overrides
// (comma list: a name turns a feature on, a leading "-" turns it off, e.g. ?flags=upgrades,-disasters); the
// host's flags travel in the room's start message so everyone plays the same game.

export interface Flags {
  /** three upgrade cards after every drop-off (skip / reroll) */
  upgrades: boolean;
  /** the good-guy / bad-guy meter: karma changes, dodging pedestrians, police chases */
  karma: boolean;
  /** natural disasters during the match */
  disasters: boolean;
  /** pedestrians and traffic on the streets */
  streetLife: boolean;
}

export const FLAG_NAMES = ['upgrades', 'karma', 'disasters', 'streetLife'] as const satisfies readonly (keyof Flags)[];

export const DEFAULT_FLAGS: Flags = { upgrades: false, karma: false, disasters: true, streetLife: true };

export const FLAG_LABELS: Record<keyof Flags, string> = { upgrades: 'Upgrades', karma: 'Karma', disasters: 'Disasters', streetLife: 'Street life' };

/** Apply a `?flags=` style list on top of a base set. Unknown names are ignored. */
export function parseFlags(spec: string | null | undefined, base: Flags = DEFAULT_FLAGS): Flags {
  const out = { ...base };
  if (!spec) return out;
  for (const raw of spec.split(/[,\s]+/)) {
    const off = raw.startsWith('-') || raw.startsWith('!'); const name = raw.replace(/^[-!]/, '').trim() as keyof Flags;
    if ((FLAG_NAMES as readonly string[]).includes(name)) out[name] = !off;
  }
  return out;
}

/** Full explicit list (every flag, on or off) so a peer with different defaults ends up identical. */
export function serializeFlags(f: Flags): string { return FLAG_NAMES.map((n) => (f[n] ? n : `-${n}`)).join(','); }

export function describeFlags(f: Flags): string {
  const off = FLAG_NAMES.filter((n) => !f[n]).map((n) => FLAG_LABELS[n]);
  return off.length ? `${off.join(', ')} off` : 'everything on';
}
